import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { OneDrivePicker } from "../../../../editor/components/oneDrive/OneDrivePicker";
import {
	saveProjectOneDrive,
	selectEditor,
} from "../../../../editor/editorSlice";
import { selectFetchLogicsStatus } from "../../../../logics/logicsSlice";
import {
	closeWindow,
	WindowTypeAndPropsDefinition,
} from "../../../windowManagerSlice";
import css from "./FilenameWindow.module.css";
import { getUniqueIdMap } from "../../../../../common/generateUniqueId";
import SaveFormat from "../../../../../common/save/SaveFormat";
import Version from "../../../../../common/Version";
import { env } from "../../../../../env/env";
import { faMicrosoft } from "@fortawesome/free-brands-svg-icons";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "../../../../editor/components/tooltip/Tooltip";

export interface FilenameWindowProps {}

export type FilenameWindowTypeAndProps = WindowTypeAndPropsDefinition<
	"filename",
	FilenameWindowProps
>;

export const saveVersion: Version = {
	commit: env.frontendVersion + env.branch,
	major: 1,
	minor: 0,
	patch: 0,
};

export function FilenameWindow(props: FilenameWindowProps & { id: string }) {
	const dispatch = useAppDispatch();
	const { instance } = useMsal();
	const { t } = useTranslation();
	const logicsStatus = useAppSelector(selectFetchLogicsStatus);
	const [newName, setNewName] = useState("");
	const editor = useAppSelector(selectEditor);
	const tooltipText =
		"Se alterado o nome, será criado um novo arquivo no OneDrive";

	useEffect(() => {
		if (editor.oneDrive?.name) {
			setNewName(editor.oneDrive?.name.split(".ppf")[0]);
		}
	}, []);

	const update = useMemo(() => {
		return `${newName}.ppf` === editor.oneDrive?.name;
	}, [newName, editor.oneDrive]);

	const updateFile = () => {
		if (logicsStatus.code !== "succeeded") {
			throw new Error("Logics not loaded");
		}

		dispatch(closeWindow(props.id));

		const saveFormat: SaveFormat = {
			logicsCommitRef: logicsStatus.logics.commit,
			editor,
			saveVersion,
			idMap: getUniqueIdMap(),
		};

		if (!editor.oneDrive) return;

		dispatch(
			saveProjectOneDrive({
				saveFormat,
				instance,
				t,
				notify: true,
				driveId: editor.oneDrive?.driveId,
				itemId: editor.oneDrive?.itemId,
				update: true,
				newName: null,
			}),
		);
	};

	return (
		<div className={css.content}>
			<div style={{ display: "flex", gap: 4 }}>
				<strong>Nome do arquivo:</strong>
				<Tooltip text={tooltipText} position="bottom">
					<FontAwesomeIcon color="#dc7609" size="xs" icon={faInfoCircle} />
				</Tooltip>
			</div>
			<div style={{ display: "flex", gap: 4 }}>
				<input value={newName} onChange={(e) => setNewName(e.target.value)} />
				{update ? (
					<button
						disabled={newName == ""}
						className={css.oneDriveButton}
						onClick={updateFile}
					>
						<FontAwesomeIcon style={{ marginRight: 4 }} icon={faMicrosoft} />
						One Drive
					</button>
				) : (
					<OneDrivePicker
						modalId={props.id}
						disabled={logicsStatus.code !== "succeeded"}
						type="menu"
						newName={newName}
					/>
				)}
			</div>
		</div>
	);
}
